// The transcript pop-up: the same sheet as Helpers / Cost Breakdown, holding the request's own
// updates cards (fully open) so the transcript scrolls in one place.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "react-feather";
import RequestPublicUpdates from "./RequestPublicUpdates";
import { TranscriptInSheet, useSheetMeta, useTranscriptSheet } from "../lib/transcriptSheet";
import type { UIMessage } from "../lib/types";

export default function TranscriptSheet({ requestMessageId, messages, pending, live }: {
  requestMessageId: string; messages: UIMessage[]; pending: boolean; live: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useTranscriptSheet((s) => s.close);
  const meta = useSheetMeta();
  useEffect(() => {
    const panel = dialog.current;
    if (!panel) return;
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!panel.open) panel.showModal();
    panel.querySelector<HTMLElement>(".helpersSheet__panel")?.focus({ preventScroll: true });
    return () => {
      // The next pop-up (maybe another request or chat) never shows this one's status.
      useSheetMeta.getState().report({ name: "", phase: "", active: false });
      if (panel.open) panel.close();
      document.body.style.overflow = overflow;
      opener?.focus?.({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <dialog
      ref={dialog}
      className="helpersSheet transcriptSheet"
      aria-labelledby="transcriptSheet-title"
      onCancel={(event) => { event.preventDefault(); close(); }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <div className="helpersSheet__panel" tabIndex={-1}>
        <div className="helpersSheet__grabber" aria-hidden="true" />
        <div className="helpersSheet__view">
          <header className="helpersSheet__head">
            <div className="helpersSheet__titles">
              <h2 id="transcriptSheet-title">
                Transcript
                {meta.name && <span className="transcriptSheet__meta"><span aria-hidden="true"> · </span>{meta.name}</span>}
                {(live || meta.phase) && <span className={"transcriptSheet__meta" + (live || meta.active ? " is-active" : "")}>
                  <span aria-hidden="true"> · </span>{live ? "Working" : meta.phase}</span>}
              </h2>
            </div>
            <button type="button" className="helpersSheet__icon" aria-label="Close transcript" onClick={close}>
              <X width={20} height={20} aria-hidden="true" />
            </button>
          </header>
          <div className="helpersSheet__body transcriptSheet__body">
            <TranscriptInSheet.Provider value={true}>
              <RequestPublicUpdates requestMessageId={requestMessageId} messages={messages} pending={pending} />
            </TranscriptInSheet.Provider>
            <p className="helpersSheet__empty transcriptSheet__empty">No transcript for this request yet.</p>
          </div>
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
